/**
 * Секция «метрики» на карточке проекта (задача 89 §3.3) — опросник значений.
 *
 * Для каждого элемента адресного списка проекта показываем его метрики (из
 * каталога, §1.2/§2.3), сгруппированные по элементу. Поле следует `metric_type`:
 * число → числовой ввод (`inputMode=decimal`), выбор → `<Select>` вариантов
 * (шлём `value_option_id`, а не строку), текст → обычный инпут. Значения
 * читаются из `project_metric_values` и накладываются поверх каталога; пустое
 * значение допустимо (метрика не заполнена).
 *
 * Локальное состояние (сырые строки по id метрики) репортится наверх через
 * `onChange` — как `EquipmentTypeSection`; финальный upsert принадлежит
 * оболочке карточки (drawer сохраняет вместе с остальными правками). Это
 * «топливо прогнозирования», но прогнозирование вне scope — только ввод.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Input } from "@/components/ui/input";
import { ApiErrorNotice } from "@/components/ui/api-error-notice";
import { Select } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useProjectMetricValues } from "@/hooks/useEquipment";
import type {
  AssemblyElement,
  MetricWithOptions,
  MetricValueInput,
  ProjectMetricValue,
} from "@/api/equipment";

export interface MetricsResult {
  /** Заполненные значения — готовы к `PUT /projects/{id}/metric-values`. */
  values: MetricValueInput[];
  /** Пользователь менял значения относительно сохранённых. */
  dirty: boolean;
}

interface Props {
  projectId: number;
  /**
   * Каталог метрик по «живому» набору элементов (§3.3) — источник опросника.
   * Приходит из `useElementsMetrics(targetElements)`: тот же набор, что уходит в
   * адресный список при сохранении (инвариант «показываем = сохраняем»).
   */
  metrics: MetricWithOptions[];
  /** Живой адресный список — задаёт порядок и имена групп-элементов. */
  elements: AssemblyElement[];
  /** Репорт значений наверх; не нужен в read-only показе. */
  onChange?: (result: MetricsResult) => void;
  disabled?: boolean;
  /**
   * Read-only показ на дефолтном табе карточки (задача 103 §2.1): значения
   * рендерятся как definition-list, без инпутов; пустой набор — пунктирная
   * плашка «вид оборудования не выбран», а не `null`.
   */
  readOnly?: boolean;
  /** Показывать элементы без параметров — состав остаётся виден одной иерархией. */
  showEmptyElements?: boolean;
  /** Выбранный состав в edit-режиме; параметры снятых опций не рендерим и не сохраняем. */
  activeElementIds?: number[];
  /** Заголовок группы элемента (lock/selection controls задаёт владелец состава). */
  renderElementHeader?: (element: AssemblyElement) => React.ReactNode;
}

function runtimeElementId(metric: MetricWithOptions): number | null {
  const value = (metric as { assembly_element_id?: unknown }).assembly_element_id;
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/** Сохранённое значение метрики → отображаемая строка (пустое → `null` = «—»). */
function readValue(metric: MetricWithOptions, v: ProjectMetricValue | undefined): string | null {
  if (!v) return null;
  if (metric.metric_type === "number")
    return v.value_number != null ? String(v.value_number) : null;
  if (metric.metric_type === "choice") {
    return metric.options.find((o) => o.id === v.value_option_id)?.value ?? null;
  }
  return v.value_text != null && v.value_text.trim() !== "" ? v.value_text : null;
}

/** Сырая строка ввода → нормализованный ключ значения (пустое отбрасываем). */
function canonical(raw: Record<number, string>): string {
  const filled = Object.entries(raw)
    .filter(([, v]) => v.trim() !== "")
    .sort(([a], [b]) => Number(a) - Number(b));
  return JSON.stringify(filled);
}

function seedFromValue(
  metricType: MetricWithOptions["metric_type"],
  v: {
    value_number?: number | null;
    value_text?: string | null;
    value_option_id?: number | null;
  },
): string {
  if (metricType === "number") return v.value_number != null ? String(v.value_number) : "";
  if (metricType === "choice") return v.value_option_id != null ? String(v.value_option_id) : "";
  return v.value_text ?? "";
}

function toInput(metric: MetricWithOptions, raw: string): MetricValueInput | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const base = { assembly_element_id: metric.assembly_element_id, metric_id: metric.id };
  if (metric.metric_type === "number") {
    const n = Number(trimmed.replace(",", "."));
    if (!Number.isFinite(n)) return null;
    return { ...base, value_number: n };
  }
  if (metric.metric_type === "choice") {
    const optionId = Number(trimmed);
    if (!Number.isFinite(optionId)) return null;
    return { ...base, value_option_id: optionId };
  }
  return { ...base, value_text: raw };
}

export function ProjectMetricsSection({
  projectId,
  metrics,
  elements,
  onChange,
  disabled,
  readOnly = false,
  showEmptyElements = false,
  activeElementIds,
  renderElementHeader,
}: Props): React.ReactElement | null {
  const { t } = useTranslation();
  const valuesQuery = useProjectMetricValues(projectId, projectId > 0);
  const effectiveActiveElementIds =
    activeElementIds ?? elements.flatMap((element) => element.id ?? []);
  const activeElementIdsKey = effectiveActiveElementIds.join(",");

  const [raw, setRaw] = useState<Record<number, string>>({});
  // `null` = ещё не засеяно из сохранённых значений; иначе — канонический снимок
  // сохранённого набора для сравнения «изменено ли». В state (не ref), чтобы не
  // читать во время рендера.
  const [seededCanon, setSeededCanon] = useState<string | null>(null);

  // Сид сырых значений из сохранённых — один раз, когда значения загрузились и
  // каталог метрик доступен (иначе типы значений не резолвятся).
  useEffect(() => {
    if (seededCanon !== null) return;
    if (!valuesQuery.isSuccess || metrics.length === 0) return;
    const byMetric = new Map(metrics.map((m) => [m.id, m.metric_type]));
    const seeded: Record<number, string> = {};
    for (const v of valuesQuery.data ?? []) {
      const metricType = byMetric.get(v.metric_id);
      if (metricType === undefined) continue;
      seeded[v.metric_id] = seedFromValue(metricType, v);
    }
    // Server-data sync: сид из сохранённых значений после загрузки (один раз).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setRaw(seeded);
    setSeededCanon(canonical(seeded));
  }, [seededCanon, valuesQuery.isSuccess, metrics, valuesQuery.data]);

  const result = useMemo<MetricsResult>(() => {
    const values: MetricValueInput[] = [];
    const activeIds = new Set(
      activeElementIdsKey === "" ? [] : activeElementIdsKey.split(",").map(Number),
    );
    for (const metric of metrics) {
      const elementId = runtimeElementId(metric);
      if (elementId === null || !activeIds.has(elementId)) continue;
      const input = toInput(metric, raw[metric.id] ?? "");
      if (input) values.push(input);
    }
    return { values, dirty: seededCanon !== null && canonical(raw) !== seededCanon };
  }, [activeElementIdsKey, metrics, raw, seededCanon]);

  useEffect(() => {
    onChange?.(result);
  }, [result, onChange]);

  if (readOnly) {
    if (valuesQuery.isError) {
      return (
        <ApiErrorNotice
          error={valuesQuery.error}
          fallbackMessage={t("common.apiError.fallback")}
          className="text-destructive text-xs"
        />
      );
    }
    const elementIds = new Set(elements.flatMap((element) => element.id ?? []));
    const readGroups = elements
      .map((el) => ({ el, items: metrics.filter((m) => runtimeElementId(m) === el.id) }))
      .filter((group) => showEmptyElements || group.items.length > 0);
    const orphanItems = metrics.filter((metric) => {
      const elementId = runtimeElementId(metric);
      return elementId === null || !elementIds.has(elementId);
    });
    // Пустой набор метрик — явная пунктирная плашка (§2.1), а не «не рендерим».
    if (readGroups.length === 0 && orphanItems.length === 0) {
      return (
        <div
          data-testid="project-metrics-empty"
          className="rounded-[var(--radius-card)] border border-dashed border-[color:var(--rule-line)] bg-[var(--bg-panel-1)] px-4 py-3 text-sm text-muted-foreground"
        >
          {t("projects.params.metricsEmpty")}
        </div>
      );
    }
    if (valuesQuery.isLoading) {
      return (
        <p className="text-muted-foreground text-xs">{t("projects.equipment.metrics.loading")}</p>
      );
    }
    const valueByMetric = new Map((valuesQuery.data ?? []).map((v) => [v.metric_id, v]));
    return (
      <div className="flex flex-col gap-3" data-testid="project-metrics-section">
        {readGroups.map(({ el, items }) => (
          <ElementGroup
            key={el.id}
            testId={`project-metric-group-${el.id}`}
            header={renderElementHeader?.(el) ?? el.name}
          >
            <ReadMetricFields items={items} valueByMetric={valueByMetric} />
          </ElementGroup>
        ))}
        {orphanItems.length > 0 && (
          <ElementGroup
            testId="project-metric-group-orphan"
            header={t("projects.equipment.metrics.orphanGroup")}
            neutral
          >
            <ReadMetricFields items={orphanItems} valueByMetric={valueByMetric} />
          </ElementGroup>
        )}
      </div>
    );
  }

  if (valuesQuery.isError) {
    return (
      <ApiErrorNotice
        error={valuesQuery.error}
        fallbackMessage={t("common.apiError.fallback")}
        className="text-destructive text-xs"
      />
    );
  }
  // Ждём сид из сохранённых значений (`seededCanon`), а не только загрузку
  // запросов: иначе поля впервые отрисуются с пустыми значениями, и radix
  // Select не подхватит выставленное позже контролируемое значение.
  if (metrics.length > 0 && (valuesQuery.isLoading || seededCanon === null)) {
    return (
      <p className="text-muted-foreground text-xs">{t("projects.equipment.metrics.loading")}</p>
    );
  }

  // Группируем по элементу в порядке адресного списка; пропускаем элементы без метрик.
  const activeIds = new Set(activeElementIds ?? elements.flatMap((element) => element.id ?? []));
  const elementIds = new Set(elements.flatMap((element) => element.id ?? []));
  const groups = elements
    .map((el) => ({
      el,
      items: activeIds.has(el.id ?? -1)
        ? metrics.filter((metric) => runtimeElementId(metric) === el.id)
        : [],
    }))
    .filter((group) => showEmptyElements || group.items.length > 0);
  const orphanItems = metrics.filter((metric) => {
    const elementId = runtimeElementId(metric);
    return elementId === null || !elementIds.has(elementId);
  });
  if (groups.length === 0 && orphanItems.length === 0) return null;

  return (
    <div className="flex flex-col gap-3" data-testid="project-metrics-section">
      {groups.map(({ el, items }) => (
        <ElementGroup
          key={el.id}
          testId={`project-metric-group-${el.id}`}
          header={renderElementHeader?.(el) ?? el.name}
        >
          <EditMetricFields
            items={items}
            raw={raw}
            setRaw={setRaw}
            disabled={disabled}
            choicePlaceholder={t("projects.equipment.metrics.choicePlaceholder")}
          />
        </ElementGroup>
      ))}
      {orphanItems.length > 0 && (
        <ElementGroup
          testId="project-metric-group-orphan"
          header={t("projects.equipment.metrics.orphanGroup")}
          neutral
        >
          <EditMetricFields
            items={orphanItems}
            raw={raw}
            setRaw={setRaw}
            disabled
            choicePlaceholder={t("projects.equipment.metrics.choicePlaceholder")}
          />
        </ElementGroup>
      )}
    </div>
  );
}

function ElementGroup({
  testId,
  header,
  neutral = false,
  children,
}: {
  testId: string;
  header: React.ReactNode;
  neutral?: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <section
      data-testid={testId}
      className={cn(
        "flex min-w-0 flex-col gap-2 border-l border-[color:var(--rule-line)] pl-3",
        neutral && "tone-neutral rounded-[var(--radius-chip)] border px-3 py-2",
      )}
    >
      <div className="min-w-0 text-sm font-medium">{header}</div>
      {children}
    </section>
  );
}

function ReadMetricFields({
  items,
  valueByMetric,
}: {
  items: MetricWithOptions[];
  valueByMetric: Map<number, ProjectMetricValue>;
}): React.ReactElement | null {
  if (items.length === 0) return null;
  return (
    <div className="grid min-w-0 grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-3">
      {items.map((metric) => {
        const text = readValue(metric, valueByMetric.get(metric.id));
        return (
          <div key={metric.id} className="flex min-w-0 flex-col gap-0.5">
            <span className="text-muted-foreground font-condensed text-xs font-medium tracking-wider uppercase">
              {metric.name}
            </span>
            <span
              data-testid={`metric-read-${metric.id}`}
              className={cn(
                "text-sm break-words",
                text != null && metric.metric_type === "number"
                  ? "font-mono text-[color:var(--accent-tech)] tabular-nums"
                  : text == null && "text-muted-foreground",
              )}
            >
              {text ?? "—"}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function EditMetricFields({
  items,
  raw,
  setRaw,
  disabled,
  choicePlaceholder,
}: {
  items: MetricWithOptions[];
  raw: Record<number, string>;
  setRaw: React.Dispatch<React.SetStateAction<Record<number, string>>>;
  disabled?: boolean;
  choicePlaceholder: string;
}): React.ReactElement | null {
  if (items.length === 0) return null;
  return (
    <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((metric) => (
        <label key={metric.id} className="flex min-w-0 flex-col gap-1 text-sm">
          <span className="min-w-0 break-words">{metric.name}</span>
          {metric.metric_type === "choice" ? (
            <Select
              data-testid={`metric-${metric.id}`}
              value={raw[metric.id] ?? ""}
              onChange={(value) => setRaw((state) => ({ ...state, [metric.id]: value }))}
              placeholder={choicePlaceholder}
              disabled={disabled}
              className="h-8 min-w-0"
              options={metric.options
                .filter((option) => option.id != null)
                .map((option) => ({ value: String(option.id), label: option.value }))}
            />
          ) : (
            <Input
              data-testid={`metric-${metric.id}`}
              inputMode={metric.metric_type === "number" ? "decimal" : undefined}
              value={raw[metric.id] ?? ""}
              disabled={disabled}
              onChange={(event) =>
                setRaw((state) => ({ ...state, [metric.id]: event.target.value }))
              }
            />
          )}
        </label>
      ))}
    </div>
  );
}
